import React from 'react';
import { useNavigate } from 'react-router-dom';

import { BackrsChip, PanelNugget } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { School } from '@backrs_ui/grow-ui/src/models/models';

import { Box, Grid, Divider, SxProps, Theme } from '@mui/material';

type SchoolFormatterProps = Readonly<{
  school: School;
}>;

const SchoolFormatter = ({ school }: SchoolFormatterProps) => {
  const navigate = useNavigate();

  if (!school) return null;

  return (
    <Box sx={schoolContainer}>
      <PanelNugget
        title={school.metadata?.school_name || 'N/A'}
        body={school.relevance}
        subText={`**Highlights**: ${school.highlights}\n\n**Considerations**: ${school.considerations}`}
        size="SMALL"
        fitContent={true}
        onClick={() => {
          navigate(`${APP_ROUTE.UNIVERSITY}/${school.metadata.id}`);
        }}
        variant={{
          type: 'institution',
          url: school.metadata?.url || '',
          previewImage: school.metadata?.banner_media_url,
        }}
      />
    </Box>
  );
};

type SchoolsFormatterProps = Readonly<{
  schools: {
    safe: School[];
    target: School[];
    stretch: School[];
  };
}>;

const SchoolList = ({ schools }: SchoolsFormatterProps) => (
  <Box>
    <Grid container spacing={2}>
      <Grid item xs={4}>
        <BackrsChip
          label="Safe"
          variant={{
            type: 'TINY_TEXT',
          }}
        />
        <ul>
          {schools.safe.map((school, index) => (
            <li key={index} style={listItemStyle}>
              {school.metadata?.school_name || 'N/A'}
            </li>
          ))}
        </ul>
      </Grid>
      <Grid item xs={4}>
        <BackrsChip
          label="Target"
          variant={{
            type: 'TINY_TEXT',
          }}
        />
        <ul>
          {schools.target.map((school, index) => (
            <li key={index} style={listItemStyle}>
              {school.metadata?.school_name || 'N/A'}
            </li>
          ))}
        </ul>
      </Grid>
      <Grid item xs={4}>
        <BackrsChip
          label="Stretch"
          variant={{
            type: 'TINY_TEXT',
          }}
        />
        <ul>
          {schools.stretch.map((school, index) => (
            <li key={index} style={listItemStyle}>
              {school.metadata?.school_name || 'N/A'}
            </li>
          ))}
        </ul>
      </Grid>
    </Grid>
    <Divider sx={dividerStyle} />
  </Box>
);

export const SchoolsFormatter = ({ schools }: SchoolsFormatterProps) => {
  if (!schools) {
    return <div></div>;
  }

  const formatColleges = (schools: School[], title: string) => (
    <Box key={title} sx={schoolCategoryBox}>
      <BackrsChip
        label={title}
        variant={{
          type: 'TINY_TEXT',
        }}
      />
      {schools.map((school, index) => (
        <SchoolFormatter key={index} school={school} />
      ))}
    </Box>
  );

  return (
    <>
      <SchoolList schools={schools} />

      <Box>{schools.safe && formatColleges(schools.safe, 'Safe')}</Box>
      <Box>{schools.target && formatColleges(schools.target, 'Target')}</Box>
      <Box>{schools.stretch && formatColleges(schools.stretch, 'Stretch')}</Box>
    </>
  );
};

const schoolCategoryBox: SxProps<Theme> = {
  marginBottom: '20px',
};

const schoolContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '8px',
  paddingBottom: '8px',
};

const listItemStyle: React.CSSProperties = {
  fontSize: '10pt',
};

const dividerStyle: SxProps<Theme> = {
  marginY: '20px',
  borderBottomWidth: '1px',
};
